import { Button, Card, Col, Input, Result, Row, Typography, Select, Space, Table, message, DatePicker, Tooltip, Alert } from 'antd';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import CustomFilterDropdown from '../../components/CustomFilterDropdown';
import { CSAccessLog, CSAllAccessLogs, CSSelfAccessLog } from '../../models/cs-access-logs-interface';
import { ErrorLog } from '../../models/error-log-interface';
import { CustomerServiceQueries } from '../../queries/customer-service-queries';
import { ErrorLogQueries } from '../../queries/error-log-queries';
import { GetDateFormateStr, TimeFromDate, TimeWithTimeZone } from '../../utilites/dateTimeHelper';
import { CSUserTypeLog } from '../../models/user-type-logs';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

type Filter = {
    text: string;
    value: number | string;
  }
  

function AccessLogsPage() {
  const [logType, setLogType] = useState<string>("access");
  const [machineFilter, setMachineFilter] = useState<string | null>(null);
  const [searchFilter, setMessageFilter] = useState<string | null>(null);
  const [selectedDate, setSelectedDates] = useState<Moment[]>([moment().add(-7, 'days').startOf('day'), moment().endOf('day')]);
  const [machineList, setMachineList] = useState<string[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<CSAccessLog[] | CSSelfAccessLog[] | CSUserTypeLog[]>([]);
  const history = useHistory();

  const accessLogQuery = CustomerServiceQueries.useAccessLogsBetweenDateList(selectedDate[0], selectedDate[1]);
  const userTypeLogQuery = CustomerServiceQueries.useUserTypeLogsBetweenDateList(selectedDate[0], selectedDate[1]);
  /*const salesforceLogQuery = CustomerServiceQueries.useSalesforceLogList();*/

  /** Filter change use effect  **/
  useEffect(() => {
    if(!!accessLogQuery?.data) {
      var allLogs: CSAccessLog[] | CSSelfAccessLog[] | CSUserTypeLog[];
      if(logType == "access") {
        allLogs = [...(accessLogQuery?.data.accessLogs ?? [])];
      } else if (logType == "usertype") {
        allLogs = [...(userTypeLogQuery?.data ?? [])];
      } 
      else {
        allLogs = [...(accessLogQuery?.data.selfAccessLogs ?? [])];
      }

      if(!!searchFilter) {
        if(logType != "usertype") {
        allLogs = (allLogs as CSAccessLog[] | CSSelfAccessLog[])?.filter(l => l.csUserName.toLowerCase().includes(searchFilter.toLowerCase()) ||
          l.accessedEmail.toLowerCase().includes(searchFilter.toLowerCase())
        );
        } else if (logType == "usertype") {
          allLogs = (allLogs as CSUserTypeLog[])?.filter(l => l.csUserName.toLowerCase().includes(searchFilter.toLowerCase()) ||
          l.modifiedUserName.toLowerCase().includes(searchFilter.toLowerCase())
        );
        }
      }

      setFilteredLogs(allLogs);
    }
  }, [accessLogQuery.data, userTypeLogQuery.data, logType, searchFilter])

  const getAppName = (appId: number | string) => {
    switch(appId) {
      case 1:
        return 'Web App'
      case 2:
        return 'Admin'
      case 3: 
        return 'iOS'
      case 4:
        return 'Android'
      case 5:
        return 'CS Portal'
      case "Salesforce":
        return "Salesforce"
      default:
        return 'Api'
    }
  }

  const appFilter:Filter[] = [
    {
      value: "Web App",
      text: "Web App"
    },
    {
      value: "Admin",
      text: "Admin"
    },
    {
      value: "iOS",
      text: "iOS"
    },
    {
      value: "Android",
      text: "Android"
    }
  ]

  let columns: any[] = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => {
        return `${moment(date).format(GetDateFormateStr(1))} ${moment.utc(date).toDate().toLocaleTimeString()}`;
      },
      sorter: {
        compare: (a, b) => {
            let d1 = a.date ?? new Date("01/01/1000");
            let d2 = b.date ?? new Date("01/01/1000");
            return moment(d1).diff(moment(d2));
        }
      },
      width: 250,
      defaultSortOrder: "descend",
      sticky: true
    },
    {
      title: 'CS User',
      dataIndex: 'csUserName',
      key: 'csUserName',
      render: (csUserName: string, record: CSAccessLog | CSSelfAccessLog | CSUserTypeLog) => {
        return <Button
        type="link"
        href={`/admin/users/manage/edit/${logType == "usertype" ? (record as CSUserTypeLog).csUserId : record.userId}`}
        onClick={(e) => {
          e.preventDefault();
          history.push(`/admin/users/manage/edit/${logType == "usertype" ? (record as CSUserTypeLog).csUserId : record.userId}`)
        }}
      >
        {csUserName}
      </Button>
      },
    },
    {
      title: 'Customer User ID',
      dataIndex: logType == "usertype" ? 'userId' : 'accessedUserId',
      key: logType == "usertype" ? 'userId' : 'accessedUserId',
      width: '180px',
      render: (userId: number) => {
        return userId
      },
    },
    {
      title: 'Customer Email',
      dataIndex: logType == "usertype" ? 'modifiedUserName' : 'accessedEmail',
      key: logType == "usertype" ? 'modifiedUserName' : 'accessedEmail',
      render: (accessedEmail: string, record: CSAccessLog | CSSelfAccessLog | CSUserTypeLog) => {
        return <Button
        type="link"
        href={`/admin/user/${logType == "usertype" ? (record as CSUserTypeLog).userId : (record as CSAccessLog | CSSelfAccessLog).accessedUserId}`}
        onClick={(e) => {
          e.preventDefault();
          history.push(`/admin/user/${logType == "usertype" ? (record as CSUserTypeLog).userId : (record as CSAccessLog | CSSelfAccessLog).accessedUserId}`)
        }}
      >
        {accessedEmail}
      </Button>
      },
    }
  ];
  
  if(logType == "access") {
    columns = columns.concat([{
      title: 'App',
      dataIndex: 'applicationId',
      key: 'applicationId',
      filters: appFilter,
      onFilter: (value: string, record: CSAccessLog) => {
        let name = getAppName(record.applicationId)
        return name === value
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) => (
          <CustomFilterDropdown
              filters={filters}
              setSelectedKeys={setSelectedKeys}
              selectedKeys={selectedKeys}
              confirm={confirm}
              clearFilters={clearFilters}
              dataIndex="activeFilter"
          />
      ),
      render: (applicationId: number) => {
        return getAppName(applicationId)
      },
      sticky: true
    },{
      title: 'Ip Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: (userId: number) => {
        return userId
      },
    }]);
  }

  if(logType == "usertype") {
    columns = columns.concat([{
      title: 'Company Id',
      dataIndex: 'companyId',
      key: 'companyId',
      filters: appFilter,
    }]);
  }

  if(logType == "self" || logType == "usertype") {
    columns.push({
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (userId: number) => {
        return userId
      },
    })
  }

  const sortDescription = ():string => {
    if(logType === "access") {
      return "Showing instances of customer support users logging into customers accounts"
    } else if(logType === "self") {
      return "Showing instances of customer support users granting themselves access to customer accounts"
    }
  }


  return (
    <Card title="CS Logs">
      <Row gutter={[16, 16]}>
        <Col flex="auto">
          <Input
            placeholder='Filter by Name or Email'
            onChange={(e) => setMessageFilter(e.target.value ?? null)}
            value={searchFilter}
          />
        </Col>
        <Col>
          <Space>
            <Select
              placeholder={"Filter by App"}
              value={logType}
              onChange={(val) => setLogType(val)}
              style={{minWidth: 200}}
            >
              <Option
                value="access"
                key="access"
              >
                Customer Account Access
              </Option>
              <Option
                value="self"
                key="self"
              >
                Granted Access
              </Option>
              <Option
                value="usertype"
                key="usertype"
              >
                User Type Changes
              </Option>
            </Select>
            <Tooltip title="Select the date of the logs you want to see">
              <RangePicker 
                value={selectedDate}
                onChange={(dates) => setSelectedDates([(dates[0] as Moment).startOf('day'), (dates[1] as Moment).endOf('day')])}
                allowClear={false}
                disabledDate={(date) => date.isAfter(moment().startOf('day'))}
              />
            </Tooltip>
          </Space>
        </Col>
        <Col span={24}>
          <Alert type="info" showIcon message={sortDescription()}/>
        </Col>
        <Col span={24}>
          <Table
            columns={columns}
            size="middle"
            dataSource={filteredLogs ?? []}
            rowKey={(val) => val.id}
            scroll
            loading={userTypeLogQuery.isLoading || accessLogQuery.isLoading}
          />
        </Col>
      </Row>
    </Card>
  );
}

export default AccessLogsPage;
